/**
 * Postman-inspired stepped config for generic-rest-api (BASE_URL · auth · credential · API_SPEC).
 * Persisted shape includes ``instanceLabel`` (chip title), ``instanceId``, ``config``, ``secretRefs``.
 */
import React, { useCallback, useMemo, useState, useEffect, useLayoutEffect } from 'react';
import {
  adoptInlineSecretToRegistry,
  buildToolSecretPath,
  createSecretsRegistrySecret,
  listSecretsRegistry,
  newToolInstanceId,
  putToolSecret,
  probeGenericRestApi,
  secretsRegistryV1Enabled,
  testSecretsRegistrySecret,
  updateSecretsRegistrySecret,
} from '../../services/toolsApi';
import {
  inferWizardProbeSeed,
  normalizeGenericRestProbePath,
  parseFirstHttpRouteFromApiSpec,
  WIZARD_PROBE_PLACEHOLDER_PATH,
} from '../../services/genericRestProbe';

const PLACEHOLDER = 'REPLACE_ME';

const DEFAULT_SPEC = `### API digest (agents read this in the prompt)
Replace every ${PLACEHOLDER} below with paths and examples from **your** API.

Example style:
GET ${PLACEHOLDER}/v1/ping
POST ${PLACEHOLDER}/v1/widgets
`;

function countPlaceholders(s) {
  if (!s) return 0;
  const re = new RegExp(`\\b${PLACEHOLDER}\\b`, 'g');
  const m = s.match(re);
  return m ? m.length : 0;
}

/** Map UI auth tabs → MCP config + credential builder. */
const AUTH_META = [
  { id: 'none', label: 'No Auth', AUTH_TYPE: 'None', needsCred: false },
  { id: 'basic', label: 'Basic Auth', AUTH_TYPE: 'Basic', needsCred: true },
  { id: 'bearer', label: 'Bearer Token', AUTH_TYPE: 'Bearer', needsCred: true },
  { id: 'apikey', label: 'API Key', AUTH_TYPE: 'Header', needsCred: true, defaultHeader: 'x-api-key' },
];

function registryRowAttachable(row, networkIdRaw) {
  const nid = String(networkIdRaw || '').trim();
  if (!nid || nid === 'new') return true;
  const s = row.scope || {};
  const k = String(s.kind || 'TENANT').toUpperCase();
  if (k === 'TENANT') return true;
  if (k === 'NETWORK') {
    const rowNid = String(s.networkId ?? s.network_id ?? '').trim();
    return rowNid === nid;
  }
  return false;
}

/** Mirrors ``secrets_registry_impl`` list rows: ``testStatus`` after ``POST …/test``. */
function registrySecretTestPassed(row) {
  return String(row?.testStatus || '').trim().toUpperCase() === 'TESTED_OK';
}

/** Sync attach vs wizard before paint so agent flow does not flash the full wizard. */
function computeInitialUiMode(persistTarget, isNewEntry, initialEntry) {
  if (persistTarget === 'registry') return 'wizard';
  if (persistTarget === 'agent' && secretsRegistryV1Enabled()) {
    const sid = initialEntry?.secretId;
    if (!isNewEntry && !sid) return 'wizard';
    return 'attach';
  }
  return 'wizard';
}

export default function ToolGenericRestConfigWizard({
  tool,
  open,
  isNewEntry,
  initialEntry,
  networkId,
  agentId,
  env,
  tenantId,
  onSave = () => {},
  onCancel,
  persistTarget = 'agent',
  registryScope,
  onRegistryCreated,
  allowProbe = true,
}) {
  const [instanceId] = useState(() => initialEntry?.instanceId || newToolInstanceId());

  const seedCfg = initialEntry?.config || {};
  const inferAuthId = () => {
    const t = String(seedCfg.AUTH_TYPE || 'Bearer').trim();
    if (t === 'None') return 'none';
    if (t === 'Basic') return 'basic';
    if (t === 'Bearer') return 'bearer';
    if (t === 'Header') return 'apikey';
    return 'bearer';
  };

  const [authId, setAuthId] = useState(inferAuthId);
  const [basicUser, setBasicUser] = useState('');
  const [basicPass, setBasicPass] = useState('');
  const [bearerToken, setBearerToken] = useState('');
  const [apiKeyHeader, setApiKeyHeader] = useState(
    () => (inferAuthId() === 'apikey' ? String(seedCfg.CUSTOM_HEADER_NAME || 'x-api-key') : 'x-api-key'),
  );
  const [apiKeyValue, setApiKeyValue] = useState('');

  const [baseUrl, setBaseUrl] = useState(
    () => String(seedCfg.BASE_URL || `https://${PLACEHOLDER}.example.com`).trim(),
  );
  const [apiSpec, setApiSpec] = useState(() =>
    typeof seedCfg.API_SPECIFICATION === 'string' ? seedCfg.API_SPECIFICATION : DEFAULT_SPEC);

  /** probeRequest — persisted on registry rows as ``PROBE_METHOD`` / ``PROBE_PATH`` */
  const _seedProbe = inferWizardProbeSeed(seedCfg);
  const [probeMethod, setProbeMethod] = useState(() => _seedProbe.method);
  const [probePath, setProbePath] = useState(() => _seedProbe.path);

  const [instanceLabel, setInstanceLabel] = useState(() =>
    String(initialEntry?.instanceLabel || '').trim(),
  );
  /** New entries must run test or skip before naming; edits may proceed without re-probing. */
  const [probeGate, setProbeGate] = useState(() => !isNewEntry);

  const hasStoredSecret = !!(initialEntry?.secretRefs?.AUTH_CREDENTIAL && !isNewEntry);
  /** When true we keep the backend secret ref and skip plaintext entry. Probes disabled until typed. */
  const [reuseStoredCredential, setReuseStoredCredential] = useState(hasStoredSecret);

  const [wizardStepIndex, setWizardStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probeNote, setProbeNote] = useState('');
  const [error, setError] = useState('');
  const [uiMode, setUiMode] = useState(() =>
    computeInitialUiMode(persistTarget, isNewEntry, initialEntry),
  ); /* wizard | attach */
  const [registryRows, setRegistryRows] = useState([]);
  const [registryAttachLoading, setRegistryAttachLoading] = useState(false);
  const [registryAttachError, setRegistryAttachError] = useState('');
  const [selectedRegistryId, setSelectedRegistryId] = useState('');

  const registryRowsTestOk = useMemo(
    () => (Array.isArray(registryRows) ? registryRows.filter(registrySecretTestPassed) : []),
    [registryRows],
  );

  const unverifiedMountedRow = useMemo(() => {
    if (isNewEntry || !initialEntry?.secretId || !registryRows.length) return null;
    const sid = String(initialEntry.secretId || '').trim();
    const row = registryRows.find((r) => String(r.secretId || '').trim() === sid);
    if (!row || registrySecretTestPassed(row)) return null;
    return row;
  }, [isNewEntry, initialEntry?.secretId, registryRows]);

  useLayoutEffect(() => {
    if (!open) return;
    setUiMode(computeInitialUiMode(persistTarget, isNewEntry, initialEntry));
  }, [open, persistTarget, isNewEntry, initialEntry?.secretId]);

  useEffect(() => {
    if (!open) {
      setError('');
      setProbeNote('');
      setRegistryRows([]);
      setRegistryAttachError('');
      setSelectedRegistryId('');
      setAdopting(false);
      return;
    }
    setWizardStepIndex(0);
    setProbeNote('');
    setReuseStoredCredential(!!hasStoredSecret);
    setInstanceLabel(String(initialEntry?.instanceLabel || '').trim());
    setProbeGate(!isNewEntry);
  }, [open, hasStoredSecret, isNewEntry, initialEntry?.instanceLabel]);

  useEffect(() => {
    if (!open || persistTarget !== 'registry') return undefined;
    if (!secretsRegistryV1Enabled()) {
      onCancel();
      return undefined;
    }
    return undefined;
  }, [open, persistTarget, onCancel]);

  useEffect(() => {
    if (!open || persistTarget !== 'agent' || !tool?.name) return undefined;
    if (!secretsRegistryV1Enabled()) {
      setRegistryRows([]);
      setUiMode('wizard');
      setRegistryAttachLoading(false);
      setRegistryAttachError('');
      return undefined;
    }
    let cancelled = false;
    setRegistryAttachLoading(true);
    setRegistryAttachError('');
    (async () => {
      try {
        // Pass networkId so the backend returns this network's inheritance chain
        // (TENANT + matching NETWORK). The unfiltered tenant surface now excludes
        // NETWORK rows (down-only), so omitting networkId here would hide a
        // network-scoped connection from its own attach picker.
        const _nid = String(networkId || '').trim();
        const { secrets } = await listSecretsRegistry({
          toolName: tool.name,
          networkId: _nid && _nid !== 'new' ? _nid : undefined,
        });
        if (cancelled) return;
        const vis = Array.isArray(secrets) ? secrets.filter((r) => registryRowAttachable(r, networkId)) : [];
        setRegistryRows(vis);

        const testOkRows = vis.filter(registrySecretTestPassed);
        const editingSid =
          !isNewEntry && initialEntry?.secretId ? String(initialEntry.secretId).trim() : '';

        let initialPick = '';
        if (editingSid) {
          const curRow = vis.find((r) => String(r.secretId || '').trim() === editingSid);
          if (curRow && registrySecretTestPassed(curRow)) {
            initialPick = editingSid;
          }
        }
        if (!initialPick && testOkRows.length >= 1) {
          initialPick = String(testOkRows[0].secretId || '');
        }
        setSelectedRegistryId(initialPick);
      } catch (e) {
        if (!cancelled) {
          setRegistryAttachError(e?.message || 'Could not load saved secrets');
          setRegistryRows([]);
        }
      } finally {
        if (!cancelled) setRegistryAttachLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, persistTarget, isNewEntry, tool?.name, networkId, initialEntry?.secretId]);

  const authMeta = useMemo(() => AUTH_META.find((a) => a.id === authId) || AUTH_META[2], [authId]);

  const credentialValue = useMemo(() => {
    if (!authMeta.needsCred) return '';
    if (authId === 'basic') {
      const u = basicUser.trim();
      const p = basicPass.trim();
      if (!u && !p) return '';
      return `${u}:${p}`;
    }
    if (authId === 'bearer') return bearerToken.trim();
    if (authId === 'apikey') return apiKeyValue.trim();
    return '';
  }, [authId, authMeta.needsCred, basicUser, basicPass, bearerToken, apiKeyValue]);

  const builtConfig = useMemo(
    () => ({
      BASE_URL: baseUrl.trim(),
      AUTH_TYPE: authMeta.AUTH_TYPE,
      CUSTOM_HEADER_NAME: authMeta.AUTH_TYPE === 'Header' ? apiKeyHeader.trim() || authMeta.defaultHeader : '',
      API_SPECIFICATION: apiSpec,
    }),
    [apiKeyHeader, apiSpec, authMeta.AUTH_TYPE, authMeta.defaultHeader, baseUrl, authMeta],
  );

  const steps = useMemo(() => {
    const s = [{ key: 'auth', title: 'Authorization' }];
    if (authMeta.needsCred) s.push({ key: 'cred', title: authMeta.label });
    s.push(
      { key: 'url', title: 'Base URL' },
      { key: 'spec', title: 'API specification' },
      { key: 'probe', title: 'Test connection' },
      { key: 'finish', title: 'Name & save' },
    );
    return s;
  }, [authMeta.needsCred, authMeta.label]);

  const maxStepIdx = steps.length - 1;

  const probeStepIndex = useMemo(() => steps.findIndex((s) => s.key === 'probe'), [steps]);

  /** Registry edits already have a name; saving from the probe step is expected after ``Test connection``. */
  const registryEditSid = persistTarget === 'registry' ? String(initialEntry?.secretId || '').trim() : '';

  const saveEligibleStep =
    wizardStepIndex === maxStepIdx ||
    (Boolean(registryEditSid) &&
      probeStepIndex >= 0 &&
      wizardStepIndex >= probeStepIndex);

  /** Need new plaintext credential before save / probe continues. */
  const needsEnteredCredential =
    authMeta.needsCred && !reuseStoredCredential && credentialValue.length === 0;

  const probePrecheckReady =
    Boolean(builtConfig.BASE_URL && builtConfig.API_SPECIFICATION) &&
    !needsEnteredCredential &&
    (authMeta.AUTH_TYPE !== 'Header' ||
      Boolean((builtConfig.CUSTOM_HEADER_NAME || '').trim()));

  /** Editing a registry row with “reuse stored credential”: inline ``POST /tools/probe`` has no secret — use registry test broker instead. */
  const registryReuseServerProbe =
    persistTarget === 'registry' &&
    Boolean(String(initialEntry?.secretId || '').trim()) &&
    reuseStoredCredential &&
    authMeta.needsCred;

  const canAdvanceFromStep = useCallback(
    (idx) => {
      const step = steps[idx];
      if (!step) return false;
      if (step.key === 'auth') return true;
      if (step.key === 'cred') return authMeta.needsCred ? !needsEnteredCredential : true;
      if (step.key === 'url') return baseUrl.trim().length > 0;
      if (step.key === 'spec') return apiSpec.trim().length > 0;
      if (step.key === 'probe') return isNewEntry ? probeGate : true;
      if (step.key === 'finish') return instanceLabel.trim().length > 0;
      return true;
    },
    [
      apiSpec,
      authMeta.needsCred,
      baseUrl,
      needsEnteredCredential,
      steps,
      isNewEntry,
      probeGate,
      instanceLabel,
    ],
  );

  const canSaveGlobally =
    Boolean(builtConfig.BASE_URL && builtConfig.API_SPECIFICATION) &&
    !needsEnteredCredential &&
    (authMeta.AUTH_TYPE !== 'Header' ||
      Boolean((builtConfig.CUSTOM_HEADER_NAME || '').trim())) &&
    !submitting &&
    saveEligibleStep &&
    instanceLabel.trim().length > 0;

  const adoptionEligible = useMemo(() => {
    if (!secretsRegistryV1Enabled()) return false;
    if (persistTarget !== 'agent' || uiMode !== 'wizard') return false;
    const nid = String(networkId || '').trim();
    const aid = String(agentId || '').trim();
    if (!nid || nid === 'new' || !aid) return false;
    if (initialEntry?.secretId) return false;
    const refs = initialEntry?.secretRefs;
    if (!refs || typeof refs !== 'object') return false;
    return Object.keys(refs).length > 0;
  }, [
    persistTarget,
    uiMode,
    networkId,
    agentId,
    initialEntry?.secretId,
    initialEntry?.secretRefs,
  ]);
  const finalizeSave = async () => {
    if (persistTarget === 'registry' && !secretsRegistryV1Enabled()) {
      setError('Secrets registry is disabled in this environment.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      if (persistTarget === 'registry') {
        const cfgFlat = {
          BASE_URL: builtConfig.BASE_URL,
          AUTH_TYPE: builtConfig.AUTH_TYPE,
          API_SPECIFICATION: builtConfig.API_SPECIFICATION,
          PROBE_METHOD: String(probeMethod || 'GET').trim().toUpperCase(),
          PROBE_PATH: normalizeGenericRestProbePath(probePath),
        };
        if (builtConfig.AUTH_TYPE === 'Header') {
          cfgFlat.CUSTOM_HEADER_NAME = builtConfig.CUSTOM_HEADER_NAME;
        }
        const plaintextSecrets =
          authMeta.needsCred && credentialValue
            ? { AUTH_CREDENTIAL: String(credentialValue) }
            : {};
        const scope =
          registryScope && typeof registryScope === 'object' ? registryScope : { kind: 'TENANT' };

        const registryEditId = String(initialEntry?.secretId || '').trim();
        if (registryEditId) {
          const patch = {
            secretName: instanceLabel.trim(),
            authType: authMeta.AUTH_TYPE,
            config: cfgFlat,
            ...(Object.keys(plaintextSecrets).length ? { plaintextSecrets } : {}),
          };
          const lv = initialEntry?.registryLogicalVersion;
          const ifMatchOpt =
            typeof lv === 'number' && Number.isFinite(lv) ? { ifMatch: String(Math.trunc(lv)) } : {};
          const data = await updateSecretsRegistrySecret(registryEditId, patch, ifMatchOpt);
          const updated = data?.secret && typeof data.secret === 'object' ? data.secret : null;
          if (!updated) {
            throw new Error('Unexpected response from secrets registry');
          }
          onRegistryCreated?.(updated);
          return;
        }

        const data = await createSecretsRegistrySecret({
          toolName: tool.name,
          secretName: instanceLabel.trim(),
          authType: authMeta.AUTH_TYPE,
          config: cfgFlat,
          ...(Object.keys(plaintextSecrets).length ? { plaintextSecrets } : {}),
          scope,
        });
        const created = data?.secret && typeof data.secret === 'object' ? data.secret : null;
        if (!created) {
          throw new Error('Unexpected response from secrets registry');
        }
        onRegistryCreated?.(created);
        return;
      }

      const config = {
        BASE_URL: builtConfig.BASE_URL,
        AUTH_TYPE: builtConfig.AUTH_TYPE,
        API_SPECIFICATION: builtConfig.API_SPECIFICATION,
      };
      if (builtConfig.AUTH_TYPE === 'Header') {
        config.CUSTOM_HEADER_NAME = builtConfig.CUSTOM_HEADER_NAME;
      }
      const secretRefs = { ...(initialEntry?.secretRefs || {}) };

      if (!authMeta.needsCred) {
        delete secretRefs.AUTH_CREDENTIAL;
      } else if (reuseStoredCredential && initialEntry?.secretRefs?.AUTH_CREDENTIAL) {
        secretRefs.AUTH_CREDENTIAL = initialEntry.secretRefs.AUTH_CREDENTIAL;
      } else if (credentialValue) {
        const parameterName = buildToolSecretPath({
          env,
          tenantId,
          networkId,
          agentId,
          instanceId,
          configKey: 'AUTH_CREDENTIAL',
        });
        await putToolSecret({ parameterName, value: String(credentialValue) });
        secretRefs.AUTH_CREDENTIAL = parameterName;
      } else {
        delete secretRefs.AUTH_CREDENTIAL;
      }

      onSave({
        name: tool.name,
        instanceId,
        instanceLabel: instanceLabel.trim(),
        config,
        secretRefs,
      });
    } catch (exc) {
      setError(exc?.message || 'Failed to save tool config');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAdoptToRegistry = useCallback(async () => {
    if (!secretsRegistryV1Enabled()) {
      setError('Secrets registry is disabled in this environment.');
      return;
    }
    if (!adoptionEligible || !tool?.name) return;
    const label = instanceLabel.trim();
    if (!label) {
      setError('Enter a registry display name (connection label) before promoting this secret.');
      return;
    }
    setAdopting(true);
    setError('');
    try {
      const data = await adoptInlineSecretToRegistry({
        networkId: String(networkId).trim(),
        agentId: String(agentId).trim(),
        instanceId: initialEntry?.instanceId || instanceId,
        toolName: tool.name,
        secretName: label,
        replaceInline: true,
      });
      const row = data?.secret && typeof data.secret === 'object' ? data.secret : null;
      if (!row?.secretId) {
        throw new Error('Unexpected response from adopt');
      }
      try {
        window.dispatchEvent(
          new CustomEvent('stepflow-secret-registry-changed', { detail: { secretId: row.secretId } }),
        );
      } catch {
        /* ignore broadcast failures */
      }
      onSave({
        name: tool.name,
        instanceId: initialEntry?.instanceId || instanceId,
        instanceLabel: String(row.secretName || label).trim(),
        secretId: row.secretId,
        secretName: row.secretName,
      });
    } catch (exc) {
      setError(exc?.message || 'Failed to save as reusable secret');
    } finally {
      setAdopting(false);
    }
  }, [
    adoptionEligible,
    agentId,
    instanceId,
    initialEntry?.instanceId,
    instanceLabel,
    networkId,
    onSave,
    tool?.name,
  ]);

  const confirmRegistryAttach = () => {
    const row = registryRowsTestOk.find((r) => String(r.secretId) === String(selectedRegistryId));
    if (!row) {
      setRegistryAttachError('Pick a connection that passed Test in Connections.');
      return;
    }
    if (!registrySecretTestPassed(row)) {
      setRegistryAttachError('Only connections with Test OK can be mounted on an agent.');
      return;
    }
    setRegistryAttachError('');
    const label = String(row.secretName || '').trim() || String(row.secretId || '').slice(0, 8);
    onSave({
      name: tool.name,
      instanceId: isNewEntry ? instanceId : initialEntry?.instanceId || instanceId,
      instanceLabel: label,
      secretId: row.secretId,
      secretName: row.secretName,
    });
  };

  const runProbe = async () => {
    setProbing(true);
    setProbeNote('');
    try {
      if (!allowProbe) {
        setProbeGate(true);
        setProbeNote(
          'Live probing is disabled in this context — use Skip test to continue registering the secret.',
        );
        return;
      }

      const pr = {
        method: String(probeMethod || 'GET').trim(),
        path: normalizeGenericRestProbePath(probePath),
      };

      if (registryReuseServerProbe) {
        const sid = String(initialEntry?.secretId || '').trim();
        const out = await testSecretsRegistrySecret(sid, {
          networkId,
          probeRequest: pr,
        });
        setProbeGate(true);
        if (out?.lambdaError) {
          setProbeNote(`Probe failed before HTTP: ${out.lambdaError}`);
          return;
        }
        const ts = String(out?.testStatus || '').toUpperCase();
        const failed = out?.ok === false || ts === 'TESTED_FAIL';
        if (failed) {
          const hint =
            (typeof out.lastTestError === 'string' && out.lastTestError.trim()) ||
            (typeof out.message === 'string' && out.message.trim()) ||
            '';
          const r = out?.result || {};
          setProbeNote(
            hint ||
              (typeof r.error === 'string'
                ? r.error
                : `Request completed with issues (${JSON.stringify(r).slice(0, 400)})`),
          );
          return;
        }
        const st = typeof out?.result?.status === 'number' ? out.result.status : '—';
        setProbeNote(`Success — upstream returned HTTP ${st}.`);
        return;
      }

      const out = await probeGenericRestApi({
        networkId,
        config: builtConfig,
        credential: credentialValue,
        probeRequest: pr,
      });
      setProbeGate(true);
      if (out.lambdaError) {
        setProbeNote(`Probe failed before HTTP: ${out.lambdaError}`);
        return;
      }
      if (!out.ok) {
        const r = out.result || {};
        setProbeNote(
          typeof r.error === 'string'
            ? r.error
            : `Request completed with issues (${JSON.stringify(r).slice(0, 400)})`,
        );
        return;
      }
      const st = typeof out.result?.status === 'number' ? out.result.status : '—';
      setProbeNote(`Success — upstream returned HTTP ${st}.`);
    } catch (e) {
      setProbeGate(true);
      setProbeNote(e?.message || 'Test connection failed');
    } finally {
      setProbing(false);
    }
  };

  const goNext = () => {
    if (!(wizardStepIndex < maxStepIdx && canAdvanceFromStep(wizardStepIndex))) return;
    const nextIdx = wizardStepIndex + 1;
    const curStep = steps[wizardStepIndex];
    const nextStep = steps[nextIdx];
    if (curStep?.key === 'spec' && nextStep?.key === 'probe') {
      const hinted = parseFirstHttpRouteFromApiSpec(apiSpec);
      const stillPlaceholder =
        String(probePath).includes(PLACEHOLDER) || probePath === WIZARD_PROBE_PLACEHOLDER_PATH;
      if (hinted && stillPlaceholder) {
        setProbeMethod(hinted.method);
        setProbePath(hinted.path);
      }
    }
    setWizardStepIndex(nextIdx);
  };
  const goBack = () => setWizardStepIndex((i) => Math.max(0, i - 1));

  if (!open || !tool) return null;

  const step = steps[wizardStepIndex];

  const phBase = countPlaceholders(baseUrl);
  const phSpec = countPlaceholders(apiSpec);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tool-config-title-generic-rest"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="flex w-full max-w-xl flex-col rounded-lg bg-white shadow-xl max-h-[90vh]">
        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 id="tool-config-title-generic-rest" className="text-base font-semibold text-gray-900">
              {persistTarget === 'registry'
                ? initialEntry?.secretId
                  ? 'Edit reusable tool secret'
                  : 'Register reusable tool secret'
                : uiMode === 'attach'
                  ? 'Attach saved secret'
                  : 'Configure external API — Postman-style'}
            </h2>
            <p className="mt-1 text-xs text-gray-600">
              {persistTarget === 'registry'
                ? tool.description
                : uiMode === 'attach'
                  ? 'Pick a saved connection for generic-rest-api. Create or edit connections under Connections in the workbench first.'
                  : tool.description}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            className="px-2.5 py-1 text-xs font-medium text-gray-500 rounded-md hover:bg-gray-100"
          >
            ×
          </button>
        </div>

        {persistTarget === 'agent' && uiMode === 'attach' ? null : (
        <div className="shrink-0 border-b border-gray-100 bg-slate-50 px-6 py-2">
          <div className="flex flex-wrap gap-1">
            {steps.map((s, idx) => (
              <button
                key={`${s.key}-${idx}`}
                type="button"
                disabled={
                  idx !== wizardStepIndex &&
                  !Array.from({ length: idx }, (_, j) => j).every((j) => canAdvanceFromStep(j))
                }
                onClick={() => setWizardStepIndex(idx)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
                  idx === wizardStepIndex
                    ? 'bg-blue-600 text-white ring-blue-700'
                    : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50'
                } disabled:opacity-40`}
              >
                {idx + 1}. {s.title}
              </button>
            ))}
          </div>
        </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {persistTarget === 'agent' && uiMode === 'attach' ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-600">
                Reuse credential metadata saved for this tenant. Only connections that show <strong className="font-medium text-gray-800">Test OK</strong> in{' '}
                <strong className="font-medium text-gray-800">Connections</strong> appear here; agents mint a fresh tool instance id on mount.
              </p>
              {registryAttachLoading ? (
                <p className="text-xs text-gray-500">Loading registry…</p>
              ) : null}
              {registryAttachError ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {registryAttachError}
                </div>
              ) : null}
              {!registryAttachLoading && unverifiedMountedRow ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950">
                  This agent&apos;s mount &quot;{String(unverifiedMountedRow.secretName || unverifiedMountedRow.secretId).slice(0, 80)}
                  &quot; has not passed Connection Test or last test failed — open Connections, run{' '}
                  <strong className="font-medium">Test</strong>, then pick a verified connection below (or remediate this one).
                </div>
              ) : null}
              {registryRows.length === 0 ? (
                <p className="text-xs text-gray-500">
                  No saved connections for this tool yet. Open <strong className="font-medium text-gray-700">Connections</strong> in the workbench, add a generic REST connection, then return here to mount it on this agent.
                </p>
              ) : registryRowsTestOk.length === 0 ? (
                <p className="text-xs text-gray-500">
                  {registryRows.length === 1
                    ? 'This connection exists but has not passed Connection Test yet. Open Connections, run '
                    : 'Connections exist for this tool, but none have passed Connection Test yet. Open Connections and run '}
                  <strong className="font-medium text-gray-700">Test</strong> on the row until it reports OK, then return here.
                </p>
              ) : (
                <label className="block space-y-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Verified connections (Test OK)</span>
                  <select
                    value={selectedRegistryId}
                    onChange={(e) => setSelectedRegistryId(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    data-testid="generic-rest-registry-attach-select"
                  >
                    {registryRowsTestOk.map((r) => (
                      <option key={String(r.secretId)} value={String(r.secretId)}>
                        {(r.secretName || r.secretId || '').slice(0, 80)}
                        {r.toolName ? ` (${r.toolName})` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {!registryAttachLoading &&
              selectedRegistryId &&
              registryRowsTestOk.length > 0 &&
              !registryRowsTestOk.some((r) => String(r.secretId) === String(selectedRegistryId)) &&
              registryRows.some((r) => String(r.secretId) === String(selectedRegistryId)) ? (
                <p className="text-[11px] text-amber-800">
                  Selected id isn&apos;t verified for agent mount — choose a Connection Test OK row or run Test first.
                </p>
              ) : null}
              {!registryAttachLoading &&
              selectedRegistryId &&
              registryRows.length > 0 &&
              !registryRows.some((r) => String(r.secretId) === String(selectedRegistryId)) ? (
                <p className="text-[11px] text-amber-800">
                  Previously selected saved secret isn&apos;t available for this network — pick another row from Connections.
                </p>
              ) : null}
            </div>
          ) : null}
          {!(persistTarget === 'agent' && uiMode === 'attach') ? (
            <>
              {step?.key === 'auth' ? (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Choose authorization</p>
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Authorization type">
                {AUTH_META.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    role="tab"
                    aria-selected={authId === a.id}
                    data-testid={`generic-rest-auth-${a.id}`}
                    onClick={() => setAuthId(a.id)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm transition ${
                      authId === a.id
                        ? 'border-blue-600 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] leading-relaxed text-gray-500">
                Same taxonomy as Postman’s <strong className="font-medium text-gray-700">Authorization</strong> tab.
              </p>
            </div>
          ) : null}

          {step?.key === 'cred' ? (
            <div className="space-y-3">
              {hasStoredSecret ? (
                <label className="flex items-start gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={reuseStoredCredential}
                    onChange={(e) => setReuseStoredCredential(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-blue-600"
                  />
                  <span>Reuse existing stored credential (recommended). Uncheck to mint a new secret.</span>
                </label>
              ) : null}

              {reuseStoredCredential && authMeta.needsCred ? (
                <div className="rounded-md bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                  <p className="mb-2">
                    Using the on-disk SecureString — values below apply only to <strong className="font-medium text-gray-800">Test connection</strong> and are not saved while Reuse stays checked.
                  </p>
                  <div className="rounded border border-dashed border-gray-200 bg-white p-2 space-y-2">
                    {authId === 'bearer' ? (
                      <div>
                        <label className="mb-1 block text-[10px] font-medium uppercase text-gray-500">Transient token</label>
                        <input
                          type="password"
                          value={bearerToken}
                          onChange={(e) => setBearerToken(e.target.value)}
                          data-testid="generic-rest-probe-only-token"
                          autoComplete="new-password"
                          className="block w-full rounded border border-gray-200 px-2 py-1 font-mono text-[11px]"
                          placeholder="Bearer token — probe only"
                        />
                      </div>
                    ) : null}
                    {authId === 'basic' ? (
                      <>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium uppercase text-gray-500">Username (probe only)</label>
                          <input
                            type="text"
                            autoComplete="off"
                            value={basicUser}
                            onChange={(e) => setBasicUser(e.target.value)}
                            data-testid="generic-rest-probe-only-basic-user"
                            className="block w-full rounded border border-gray-200 px-2 py-1 text-[11px]"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-[10px] font-medium uppercase text-gray-500">Password (probe only)</label>
                          <input
                            type="password"
                            value={basicPass}
                            autoComplete="new-password"
                            onChange={(e) => setBasicPass(e.target.value)}
                            data-testid="generic-rest-probe-only-basic-pass"
                            className="block w-full rounded border border-gray-200 px-2 py-1 text-[11px]"
                          />
                        </div>
                      </>
                    ) : null}
                    {authId === 'apikey' ? (
                      <div>
                        <label className="mb-1 block text-[10px] font-medium uppercase text-gray-500">Key value (probe only)</label>
                        <input
                          type="password"
                          value={apiKeyValue}
                          onChange={(e) => setApiKeyValue(e.target.value)}
                          data-testid="generic-rest-probe-only-apikey"
                          autoComplete="new-password"
                          className="block w-full rounded border border-gray-200 px-2 py-1 font-mono text-[11px]"
                          placeholder={`Header name from config: ${apiKeyHeader || 'x-api-key'}`}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {authId === 'basic' && !reuseStoredCredential ? (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Username</label>
                    <input
                      autoComplete="off"
                      type="text"
                      value={basicUser}
                      onChange={(e) => setBasicUser(e.target.value)}
                      data-testid="generic-rest-basic-user"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Password</label>
                    <input
                      type="password"
                      value={basicPass}
                      autoComplete="new-password"
                      onChange={(e) => setBasicPass(e.target.value)}
                      data-testid="generic-rest-basic-pass"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </>
              ) : null}

              {authId === 'bearer' && !reuseStoredCredential ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700">Token</label>
                  <input
                    type="password"
                    value={bearerToken}
                    onChange={(e) => setBearerToken(e.target.value)}
                    placeholder="Paste bearer token…"
                    data-testid="generic-rest-bearer"
                    autoComplete="new-password"
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              ) : null}

              {authId === 'apikey' && !reuseStoredCredential ? (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Key name (header)</label>
                    <input
                      type="text"
                      value={apiKeyHeader}
                      onChange={(e) => setApiKeyHeader(e.target.value)}
                      placeholder="x-api-key"
                      data-testid="generic-rest-apikey-header"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono text-[12px]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Key value</label>
                    <input
                      type="password"
                      value={apiKeyValue}
                      onChange={(e) => setApiKeyValue(e.target.value)}
                      data-testid="generic-rest-apikey-value"
                      autoComplete="new-password"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500">
                    Mirrors Postman&apos;s API Key mode → MCP AUTH_TYPE=<code className="text-[10px]">Header</code>.
                  </p>
                </>
              ) : null}
            </div>
          ) : null}

          {step?.key === 'url' ? (
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                HTTPS base URL
              </label>
              <input
                type="url"
                data-testid="generic-rest-base-url"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.example.com"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              {phBase > 0 ? (
                <p className="text-[11px] text-red-600">
                  Highlight: replace{' '}
                  <span className="rounded bg-red-50 px-1 font-mono font-semibold">{PLACEHOLDER}</span> fragments (
                  {phBase}) before production use.
                </p>
              ) : null}
            </div>
          ) : null}

          {step?.key === 'spec' ? (
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Prompt injection — API_digest
              </label>
              <textarea
                data-testid="generic-rest-api-spec"
                value={apiSpec}
                rows={14}
                onChange={(e) => setApiSpec(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-[11px] leading-snug focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              {phSpec > 0 ? (
                <p className="text-[11px] text-red-600">
                  {phSpec} &times;{' '}
                  <span className="rounded bg-red-50 px-1 font-mono">{PLACEHOLDER}</span> tokens left — personalize so the agent knows real endpoints.
                </p>
              ) : (
                <p className="text-[11px] text-emerald-700">✓ Template tokens cleared — good baseline for save.</p>
              )}
            </div>
          ) : null}

          {step?.key === 'probe' ? (
            <div className="space-y-3 rounded-lg border border-dashed border-blue-100 bg-blue-50/40 p-4">
              <p className="text-xs font-medium text-blue-950">
                {registryReuseServerProbe
                  ? 'Test connection (uses saved registry credential)'
                  : 'Smoke-test the MCP Lambda with your ephemeral credential'}
              </p>
              {registryReuseServerProbe && allowProbe ? (
                <p className="text-[11px] text-gray-700">
                  Method and path below are sent with the probe. Base URL and auth settings come from your{' '}
                  <strong className="font-medium text-gray-800">last saved</strong> registry row — press Save on the
                  final step if you changed them.
                </p>
              ) : null}
              {!allowProbe ? (
                <p className="text-[11px] text-gray-700">
                  Deployed network context isn&apos;t wired for live probes here — use <strong className="font-medium">Skip test</strong>.
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <select
                  value={probeMethod}
                  onChange={(e) => setProbeMethod(e.target.value)}
                  className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </select>
                <input
                  type="text"
                  value={probePath}
                  onChange={(e) => setProbePath(e.target.value)}
                  className="min-w-[10rem] flex-1 rounded-md border border-gray-200 px-2 py-1 font-mono text-xs"
                  placeholder="/health"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={
                    probing ||
                    !allowProbe ||
                    !probePrecheckReady ||
                    (authMeta.needsCred &&
                      reuseStoredCredential &&
                      !credentialValue.trim() &&
                      !registryReuseServerProbe)
                  }
                  onClick={runProbe}
                  data-testid="generic-rest-run-probe"
                  className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-45"
                >
                  {probing ? 'Testing…' : 'Test connection'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setProbeNote('Skipped verification — continue to name this connection.');
                    setProbeGate(true);
                  }}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium hover:bg-gray-50"
                  data-testid="generic-rest-skip-test"
                >
                  Skip test
                </button>
              </div>
              {probeNote ? <p className="text-[11px] text-gray-800">{probeNote}</p> : null}
            </div>
          ) : null}

          {step?.key === 'finish' ? (
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                {persistTarget === 'registry' ? 'Registry display name' : 'Connection display name'}
              </label>
              <p className="text-[11px] text-gray-600">
                {persistTarget === 'registry'
                  ? 'Shown under Connections and when attaching to an agent.'
                  : (
                      <>
                        Shown on the agent&apos;s tool strip. Use a distinct label when you add the same MCP tool twice
                        (e.g. &quot;Stripe prod&quot; vs &quot;Stripe sandbox&quot;).
                      </>
                    )}
              </p>
              <input
                type="text"
                data-testid="generic-rest-instance-label"
                value={instanceLabel}
                onChange={(e) => setInstanceLabel(e.target.value)}
                placeholder="e.g. Acme CRM — read-only"
                autoComplete="off"
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-[11px] text-gray-500">
                Tool id (fixed): <code className="rounded bg-gray-100 px-1 text-[10px]">{tool.name}</code>
              </p>
            </div>
          ) : null}
            </>
          ) : null}

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-6 py-3">
          {persistTarget === 'agent' && uiMode === 'attach' ? (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="generic-rest-switch-to-wizard"
                  onClick={() => {
                    setUiMode('wizard');
                    setWizardStepIndex(0);
                    setRegistryAttachError('');
                  }}
                  className="px-2.5 py-1 text-xs font-medium rounded-md border border-blue-600 bg-white text-blue-700 hover:bg-blue-50"
                >
                  Configure new instead
                </button>
              </div>
              <button
                type="button"
                data-testid="generic-rest-registry-mount"
                disabled={
                  registryAttachLoading ||
                  registryRowsTestOk.length === 0 ||
                  !registryRowsTestOk.some((r) => String(r.secretId) === String(selectedRegistryId))
                }
                onClick={confirmRegistryAttach}
                className="px-2.5 py-1 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-45 disabled:cursor-not-allowed"
              >
                Mount saved secret
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                {adoptionEligible ? (
                  <button
                    type="button"
                    data-testid="generic-rest-save-as-reusable"
                    disabled={adopting || submitting || !instanceLabel.trim()}
                    onClick={handleAdoptToRegistry}
                    className="px-2.5 py-1 text-xs font-medium rounded-md border border-emerald-700 bg-white text-emerald-800 hover:bg-emerald-50 disabled:opacity-45 disabled:cursor-not-allowed"
                  >
                    {adopting ? 'Promoting…' : 'Save as reusable secret'}
                  </button>
                ) : null}
                {wizardStepIndex > 0 ? (
                  <button
                    type="button"
                    onClick={goBack}
                    data-testid="generic-rest-wizard-back"
                    className="px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    Back
                  </button>
                ) : null}
                {wizardStepIndex < maxStepIdx ? (
                  <button
                    type="button"
                    data-testid="generic-rest-wizard-next"
                    onClick={goNext}
                    disabled={!canAdvanceFromStep(wizardStepIndex)}
                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-white border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-45"
                  >
                    Next
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                disabled={!canSaveGlobally || submitting}
                data-testid="generic-rest-save"
                onClick={finalizeSave}
                className="px-2.5 py-1 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-45 disabled:cursor-not-allowed"
              >
                {submitting
                  ? 'Saving…'
                  : persistTarget === 'registry'
                    ? initialEntry?.secretId
                      ? 'Save changes'
                      : 'Create secret'
                    : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
